import { TypeChecker } from '@attain-apps/type-guards'

import type * as ecModels from '../../../models'

import * as models from './models'
import type { Plugin } from './plugin'

export class Selectors<TTypeParams extends ecModels.TypeParams.Constraint> {
  /**
   * Lookup of all current creations
   *
   * @see {@link models.EditedEntity.Lookup}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly creationsLookup

  /**
   * Lookup of all current updates
   *
   * @see {@link models.EditedEntity.Lookup}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly updatesLookup

  /**
   * Retrieves the requested edited (creation or update) entity, if it exists
   *
   * @see {@link getCreationEntity}, {@link getUpdatedEntity}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getEditedEntity
  /**
   * Indicates the requested edited (creation or update) entity exists
   *
   * @see {@link getEditedEntity}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly hasEditedEntity

  /**
   * Retrieves the requested creation, if it exists
   *
   * @see {@link creationsLookup}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getCreationEntity
  /**
   * Indicates the requested creation exists
   *
   * @see {@link getCreationEntity}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly hasCreationEntity

  /**
   * Retrieves the requested updated entity, if it exists
   *
   * @see {@link updatesLookup}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getUpdatedEntity
  /**
   * Indicates the requested updated entity exists
   *
   * @see {@link getUpdatedEntity}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly hasUpdatedEntity

  /**
   * Retrieves the requested entity marked for deletion
   *
   * @see {@link getDeletedEntity}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getDeletedEntity

  /**
   * Retrieves all entities marked for deletion
   *
   * @see {@link deletedEntities}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly deletedEntities
  /**
   * A set of {@link ecModels.EntityId} that are marked for deletion
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly deletions

  /**
   * Indicates whether a submission is currently in progress.
   */
  // NOTE: Intentionally not typed here so that type can be inferred
  public readonly isSubmitting

  /**
   * Holds the submission error, if any.
   */
  // NOTE: Intentionally not typed here so that type can be inferred
  public readonly submissionError

  /**
   * Number of {@link ecModels.EntityId} that are marked for deletion
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly deletionCount

  /**
   * Indicates that there is at least one {@link ecModels.EntityId} that is
   * marked for deletion
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly hasDeletions

  /**
   * Determines if the given {@link ecModels.EntityId} is marked for deletion
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isMarkedForDeletion

  constructor(plugin: Plugin<TTypeParams>) {
    this.creationsLookup = plugin.feature.state.project(
      state => state.creationsLookup,
    )
    this.updatesLookup = plugin.feature.state.project(
      state => state.updatesLookup,
    )

    this.getEditedEntity = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.creationsLookup.project(
          this.updatesLookup.asSelector,
          (creationsLookup, updatesLookup) =>
            models.CreationId.isCreationId(id)
              ? creationsLookup[id]
              : updatesLookup[id],
        ).asSelector,
    )

    this.deletedEntities = plugin.feature.createSelectorMethod(() =>
      this.deletions.project(
        plugin.collection.plugins.cachePlugin.feature.selectors.lookup
          .asSelector,
        (deletions, lookup) =>
          Array.from(deletions, id => lookup[id]).filter(
            TypeChecker.isNotNullish.raw,
          ),
      ),
    )

    this.getDeletedEntity = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.deletions.project(
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asSelector,
          (deletions, lookup) => (deletions.has(id) ? lookup[id] : undefined),
        ),
    )

    this.hasEditedEntity = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.getEditedEntity(id).project(entity => !!entity).asSelector,
    )
    this.getCreationEntity = plugin.feature.createSelectorMethod(
      (id: models.CreationId) =>
        this.creationsLookup.project(lookup => lookup[id]).asSelector,
    )
    this.hasCreationEntity = plugin.feature.createSelectorMethod(
      (id: models.CreationId) =>
        this.getCreationEntity(id).project(entity => !!entity).asSelector,
    )
    this.getUpdatedEntity = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.updatesLookup.project(lookup => lookup[id]).asSelector,
    )
    this.hasUpdatedEntity = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.getUpdatedEntity(id).project(entity => !!entity).asSelector,
    )

    this.deletions = plugin.feature.state.project(state => state.deletions)

    this.isSubmitting = plugin.feature.state.project(
      state => state.isSubmitting,
    )
    this.submissionError = plugin.feature.state.project(state =>
      TypeChecker.isString(state.submissionError)
        ? state.submissionError
        : state.submissionError?.message,
    )

    this.deletionCount = this.deletions.project(deletions => deletions.size)
    this.hasDeletions = this.deletionCount.project(
      deletionCount => deletionCount > 0,
    )

    this.isMarkedForDeletion = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.deletions.project(deletions => deletions.has(id)).asSelector,
    )
  }
}
